import React, {ComponentType, useCallback, useEffect, useRef, useState} from 'react';
import {
  Button,
  FormControl,
  Grid,
  InputAdornment, InputLabel, OutlinedInput,
  Stack,
} from "@mui/material";
import {styled} from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import api from "../../../services/api";
import Box from "@mui/material/Box";
import {useStateContext} from "../../../hooks/useStateContext";
import {hasPermissionByPriority} from "../../../core/permissions/hasPermission";
import {ROLE_PRIORITIES} from "../../../core/permissions/permissions";
import {hasPermissionId} from "../../../core/permissions/hasPermissionId";
import {Prices} from "./Prices";
import {activityPrice} from "../../../schema/activityPrice";

type Props = {
  formState: any;
  setFormState: any;
  slidingRef: React.RefObject<any>;
};

const round2 = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

const Item = styled(Paper)(({theme}) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  padding: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

export const PricesSchema: ComponentType<Props> = ({
                                                       formState,
                                                       setFormState,
                                                       slidingRef,
                                                     }) => {

  const [loading, setLoading] = useState(false);
  const [prices, setPrices] = useState<any>({
    service_package: {},
    services: [],
  });

  const {user} = useStateContext();
  const isAdmin = hasPermissionByPriority(user.roles, ROLE_PRIORITIES[1]);
  const isFinance = hasPermissionId(user.roles, 101);

  const fetchPrices = useCallback(async () => {
    setLoading(true);

    try {
      const payload = {
        activity_id: formState?.activity?.id,
        date: formState?.date,
        group_id: formState?.group?.id,
        group_size: formState?.group_size,
        selected_service_group_id: formState?.selected_service_group,
        sales_partner_id: formState?.sales_partner?.id,
        type_id: formState?.type?.id,
      };

      const res = await api.post(`/services/calculate-prices`, payload);

      setPrices(res); // vagy res.data attól függ az api wrappered
    } finally {
      setLoading(false);
    }
  }, [formState]);

  useEffect(() => {
    fetchPrices();
  }, []);

  const groupSize = Number(formState?.group_size ?? 0);

  // kontrollált mezők
  const [finalPrice, setFinalPrice] = useState<string>("");     // pl "120"
  const [finalPricePP, setFinalPricePP] = useState<string>(""); // pl "12"

  // melyiket írta utoljára a user? (hogy ne loopoljon)
  const lastEditedRef = useRef<"final" | "pp" | null>(null);

  // init / refresh után töltsük be alap értékekkel (originalból indul)
  useEffect(() => {
    const orig = prices?.original_price;
    if (orig !== undefined && orig !== null && orig !== "") {
      const fp = round2(Number(orig));
      setFinalPrice(fp.toFixed(2));

      if (groupSize > 0) {
        setFinalPricePP(round2(fp / groupSize).toFixed(2));
      } else {
        setFinalPricePP("");
      }

      lastEditedRef.current = null;
    }
  }, [prices?.original_price, groupSize]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!groupSize || groupSize <= 0) return;

      if (lastEditedRef.current === "pp") {
        const pp = Number(finalPricePP);
        if (!Number.isFinite(pp)) return;

        const nextFinal = round2(pp * groupSize);

        if (Number(finalPrice) !== nextFinal) {
          setFinalPrice(String(nextFinal.toFixed(2)));
        }
      }

      if (lastEditedRef.current === "final") {
        const fp = Number(finalPrice);
        if (!Number.isFinite(fp)) return;

        const nextPP = round2(fp / groupSize);

        if (Number(finalPricePP) !== nextPP) {
          setFinalPricePP(String(nextPP.toFixed(2)));
        }
      }
    }, 200);

    return () => clearTimeout(t);
  }, [finalPrice, finalPricePP, groupSize]);

  useEffect(() => {
    setFormState((prev: any) => ({
      ...prev,
      final_price: finalPrice === "" ? null : Number(finalPrice),
      final_price_pp: finalPricePP === "" ? null : Number(finalPricePP),
    }));
  }, [finalPrice, finalPricePP, setFormState]);

  return (
    <>

      <Grid container spacing={2}>

        <Grid item xs={12} md={12}>
          <Item>
            <Prices schema={activityPrice} values={formState?.activity} />
          </Item>
        </Grid>

        <Grid item xs={12} md={12}>
          <Item>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>

                <Stack alignItems={'flex-end'}>
                  <Button
                    onClick={()=>{
                      fetchPrices();
                    }}>
                    REFRESH Prices
                  </Button>
                </Stack>

              </Grid>

              <Grid item xs={12} md={6}>
                <Item>
                  <Stack direction={'row'} justifyContent={'flex-end'} gap={1}>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                      <FormControl fullWidth sx={{ m: 1 }}>
                        <InputLabel htmlFor="original-price">Original Price</InputLabel>
                        <OutlinedInput
                          disabled
                          id="original-price"
                          startAdornment={<InputAdornment position="start">€</InputAdornment>}
                          label="Original Price"
                          value={prices?.original_price ? prices?.original_price : 0}
                        />
                      </FormControl>

                      <FormControl fullWidth sx={{ m: 1 }}>
                        <InputLabel htmlFor="original-price-pp">Original Price PP</InputLabel>
                        <OutlinedInput
                          disabled
                          id="original-price-pp"
                          startAdornment={<InputAdornment position="start">€</InputAdornment>}
                          label="Original Price PP"
                          value={prices?.original_price_pp ? prices?.original_price_pp : 0}
                        />
                      </FormControl>
                    </Box>


                    <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                      <FormControl fullWidth sx={{ m: 1 }}>
                        <InputLabel htmlFor="final-price">Final Price</InputLabel>
                        <OutlinedInput
                          disabled={loading}
                          id="final-price"
                          startAdornment={<InputAdornment position="start">€</InputAdornment>}
                          label="Final Price"
                          value={finalPrice}
                          onChange={(e) => {
                            lastEditedRef.current = "final";
                            setFinalPrice(e.target.value);
                          }}
                        />
                      </FormControl>

                      <FormControl fullWidth sx={{ m: 1 }}>
                        <InputLabel htmlFor="final-price-pp">Final Price PP</InputLabel>
                        <OutlinedInput
                          disabled={loading}
                          id="final-price-pp"
                          startAdornment={<InputAdornment position="start">€</InputAdornment>}
                          label="Final Price PP"
                          value={finalPricePP}
                          onChange={(e) => {
                            lastEditedRef.current = "pp";
                            setFinalPricePP(e.target.value);
                          }}
                        />
                      </FormControl>

                    </Box>

                  </Stack>
                </Item>
              </Grid>
            </Grid>
          </Item>
        </Grid>

      </Grid>
    </>
  );
};
